import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Descriptions,
  Card,
  Tag,
  Input,
  Space,
  message,
  Typography,
  Spin,
  Flex,
  Modal,
} from "antd";
import BackButton from "../../components/CustomButton/BackButton";
import PrimaryButton from "../../components/CustomButton/PrimaryButton";
import DangerButton from "../../components/CustomButton/DangerButton";
import CancelButton from "../../components/CustomButton/CancelButton";
import { useDispatch, useSelector } from "react-redux";
import {
  approveManualKyc,
  rejectManualKyc,
  getManualKycDetails,
} from "../../redux/slices/investorSlice";
import {
  KYC_VERIFICATION_MODE,
  MANUAL_KYC_MSG,
  MANUAL_KYC_STEP_STATUS,
} from "../../constants/constants";
import {
  displayOrPlaceholder,
  filterDocsWithFiles,
  filterManualKycStepDocs,
  formatBankNameBranch,
  formatCityState,
  formatStatePincode,
  getAsyncThunkErrorMessage,
  getBankStepMeta,
  getEntityPanLabel,
  getIdentityPanLabel,
  getRejectRemark,
  getStep1Title,
  getStep2Title,
  getStepStatusTagMeta,
  isEntityOrHufOrNbfcInvestor,
  isHufInvestor,
  MANUAL_KYC_MODE_TAG,
  resolveCompanyPanValue,
  resolveGstinValue,
  resolveUserTypeId,
  resolveUserTypeName,
} from "../../helpers/manualKycReview";
import ManualKycDocCards from "./Components/ManualKycDocCards";
import "./Investor.scss";

const { Title, Text } = Typography;

const ACTION_ROW_STYLE = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 16,
};

const rejectedBorder = (status) =>
  status === MANUAL_KYC_STEP_STATUS.REJECTED ? "#ff4d4f" : undefined;

const StepStatusTag = ({ status }) => {
  const meta = getStepStatusTagMeta(status);
  return <Tag color={meta.color}>{meta.label}</Tag>;
};

const StepActionButtons = ({ step, onReject, onApprove }) => (
  <Space style={ACTION_ROW_STYLE}>
    <DangerButton onClick={() => onReject(step)}>
      {MANUAL_KYC_MSG.REJECT_STEP(step)}
    </DangerButton>
    <PrimaryButton onClick={() => onApprove(step)}>
      {MANUAL_KYC_MSG.APPROVE_STEP(step)}
    </PrimaryButton>
  </Space>
);

const StepRejectedRemark = ({ stepLabel, remark }) => (
  <div style={{ marginTop: 16, textAlign: "right" }}>
    <Text type="danger">
      {stepLabel} Rejected: {remark}
    </Text>
  </div>
);

const ManualKycReviewScreen = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const investorData = location.state?.investorData || {};

  const [rejectModal, setRejectModal] = useState({ open: false, step: null });
  const [rejectReason, setRejectReason] = useState("");
  const [submittingReject, setSubmittingReject] = useState(false);
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const { manualKycLoading } = useSelector((state) => state.investor);

  const fetchDetails = useCallback(() => {
    if (!orgId) return;
    setLoadingDetails(true);
    dispatch(getManualKycDetails(orgId))
      .unwrap()
      .then((res) => setDetails(res.response || res))
      .catch(() => message.error(MANUAL_KYC_MSG.LOAD_FAILED))
      .finally(() => setLoadingDetails(false));
  }, [orgId, dispatch]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleApproveStep = async (stepNum) => {
    if (!orgId) return;
    try {
      const res = await dispatch(approveManualKyc({ orgId, step: stepNum }));
      if (
        res.meta.requestStatus === "fulfilled" &&
        (res.payload?.statusCode === 200 || res.payload?.statusCode === 201)
      ) {
        message.success(MANUAL_KYC_MSG.APPROVE_SUCCESS(stepNum));
        fetchDetails();
      } else {
        message.error(
          getAsyncThunkErrorMessage(res.payload, MANUAL_KYC_MSG.APPROVE_FAILED),
        );
      }
    } catch (err) {
      message.error(getAsyncThunkErrorMessage(err, MANUAL_KYC_MSG.APPROVE_FAILED));
    }
  };

  const handleOpenRejectModal = (stepNum) => {
    setRejectReason("");
    setRejectModal({ open: true, step: stepNum });
  };

  const handleCloseRejectModal = () =>
    setRejectModal({ open: false, step: null });

  const handleConfirmReject = async () => {
    if (!orgId || !rejectModal.step) return;
    if (!rejectReason?.trim()) {
      message.error(MANUAL_KYC_MSG.REJECT_REASON_REQUIRED);
      return;
    }
    setSubmittingReject(true);
    try {
      const res = await dispatch(
        rejectManualKyc({
          orgId,
          step: rejectModal.step,
          rejectReason: rejectReason.trim(),
        }),
      );
      if (
        res.meta.requestStatus === "fulfilled" &&
        (res.payload?.statusCode === 200 || res.payload?.statusCode === 201)
      ) {
        message.success(MANUAL_KYC_MSG.REJECT_SUCCESS(rejectModal.step));
        handleCloseRejectModal();
        setRejectReason("");
        fetchDetails();
      } else {
        message.error(
          getAsyncThunkErrorMessage(res.payload, MANUAL_KYC_MSG.REJECT_FAILED),
        );
      }
    } catch (err) {
      message.error(getAsyncThunkErrorMessage(err, MANUAL_KYC_MSG.REJECT_FAILED));
    } finally {
      setSubmittingReject(false);
    }
  };

  const userTypeId = resolveUserTypeId({ investorData, details });
  const userTypeName = resolveUserTypeName({ investorData, details, userTypeId });
  const isHuf = isHufInvestor(userTypeId, userTypeName);
  const isEntityOrHufOrNbfc = isEntityOrHufOrNbfcInvestor(userTypeId, userTypeName);

  const allDocs = details?.documents || [];
  const fileDocs = filterDocsWithFiles(allDocs);
  const step1Docs = filterManualKycStepDocs(fileDocs, 1, { isEntityOrHufOrNbfc });
  const step2Docs = filterManualKycStepDocs(fileDocs, 2, { isEntityOrHufOrNbfc });
  const step3Docs = filterManualKycStepDocs(fileDocs, 3);

  const bank = details?.bankDetails || {};
  const business = details?.business || {};
  const user = details?.userDetails || {};
  const address = details?.address || {};
  const billingAddress = details?.billingAddress || address;
  const primaryAddress = details?.primaryAddress || address;

  const companyPanVal = resolveCompanyPanValue(allDocs, isEntityOrHufOrNbfc);
  const gstinVal = resolveGstinValue(business, allDocs);
  const bankStep = getBankStepMeta(isEntityOrHufOrNbfc, business);

  return (
    <div style={{ padding: "24px" }}>
      <Flex align="center" gap={12} style={{ marginBottom: 24 }}>
        <BackButton onClick={() => navigate("/investor")} />
        <Title level={3} style={{ margin: 0 }}>
          Review Manual KYC -{" "}
          {user.userName || business.busName || orgId}
        </Title>
      </Flex>

      <Spin spinning={manualKycLoading || loadingDetails}>
        <Card size="small" style={{ marginBottom: 20, background: "#fafafa" }}>
          <Descriptions size="small" column={4}>
            <Descriptions.Item label="Applicant">
              {displayOrPlaceholder(investorData.investorName || user.userName)}
            </Descriptions.Item>
            <Descriptions.Item label="User Type">
              {displayOrPlaceholder(
                investorData.investorTypeName || userTypeName || user.userTypeName,
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {displayOrPlaceholder(investorData.email || user.email)}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {displayOrPlaceholder(user.mobile)}
            </Descriptions.Item>
            <Descriptions.Item label="Org ID">{orgId}</Descriptions.Item>
            <Descriptions.Item label="Verification Mode">
              <Tag color={MANUAL_KYC_MODE_TAG.color}>
                {KYC_VERIFICATION_MODE.MANUAL}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          size="small"
          title={
            <Space>
              <Text strong style={{ fontSize: 16 }}>
                {getStep1Title({ isHuf, isEntityOrHufOrNbfc })}
              </Text>
              <StepStatusTag status={business.step1Status} />
            </Space>
          }
          style={{
            marginBottom: 20,
            borderColor: rejectedBorder(business.step1Status),
          }}
        >
          <Descriptions
            bordered
            size="small"
            column={3}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Full Name">
              {displayOrPlaceholder(business.pocName || user.userName)}
            </Descriptions.Item>
            <Descriptions.Item
              label={getIdentityPanLabel({ isHuf, isEntityOrHufOrNbfc })}
            >
              {displayOrPlaceholder(business.pan)}
            </Descriptions.Item>
            <Descriptions.Item label="Address Line 1">
              {displayOrPlaceholder(billingAddress.addressLine1)}
            </Descriptions.Item>
            <Descriptions.Item label="Address Line 2">
              {displayOrPlaceholder(billingAddress.addressLine2)}
            </Descriptions.Item>
            <Descriptions.Item label="City">
              {displayOrPlaceholder(
                billingAddress.city || billingAddress.cityName,
              )}
            </Descriptions.Item>
            <Descriptions.Item label="State / Pincode">
              {formatStatePincode(billingAddress)}
            </Descriptions.Item>
          </Descriptions>

          <Title level={5} style={{ fontSize: 14, marginBottom: 12 }}>
            Uploaded Documents
          </Title>
          <ManualKycDocCards
            docList={step1Docs}
            isEntityOrHufOrNbfc={isEntityOrHufOrNbfc}
            isHuf={isHuf}
          />

          {business.step1Status === MANUAL_KYC_STEP_STATUS.SUBMITTED && (
            <StepActionButtons
              step={1}
              onReject={handleOpenRejectModal}
              onApprove={handleApproveStep}
            />
          )}
          {business.step1Status === MANUAL_KYC_STEP_STATUS.REJECTED && (
            <StepRejectedRemark
              stepLabel="Step 1"
              remark={getRejectRemark(business.step1Remarks, business.remarks)}
            />
          )}
        </Card>

        {isEntityOrHufOrNbfc && (
          <Card
            size="small"
            title={
              <Space>
                <Text strong style={{ fontSize: 16 }}>
                  {getStep2Title({ isHuf })}
                </Text>
                <StepStatusTag status={business.step2Status} />
              </Space>
            }
            style={{
              marginBottom: 20,
              borderColor: rejectedBorder(business.step2Status),
            }}
          >
            <Descriptions
              bordered
              size="small"
              column={3}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Business Name">
                {displayOrPlaceholder(business.busName)}
              </Descriptions.Item>
              {!isHuf && (
                <Descriptions.Item label="GSTIN">{gstinVal}</Descriptions.Item>
              )}
              <Descriptions.Item label={getEntityPanLabel({ isHuf })}>
                {companyPanVal}
              </Descriptions.Item>
              <Descriptions.Item label="POC Name">
                {displayOrPlaceholder(business.pocName)}
              </Descriptions.Item>
              <Descriptions.Item label="Registered Address Line 1">
                {displayOrPlaceholder(primaryAddress.addressLine1)}
              </Descriptions.Item>
              <Descriptions.Item label="Registered City / State">
                {formatCityState(primaryAddress)}
              </Descriptions.Item>
              <Descriptions.Item label="Pincode">
                {displayOrPlaceholder(
                  primaryAddress.postalCode || primaryAddress.zipCode,
                )}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ fontSize: 14, marginBottom: 12 }}>
              Uploaded Documents
            </Title>
            <ManualKycDocCards
              docList={step2Docs}
              isEntityOrHufOrNbfc={isEntityOrHufOrNbfc}
              isHuf={isHuf}
            />

            {business.step2Status === MANUAL_KYC_STEP_STATUS.SUBMITTED && (
              <StepActionButtons
                step={2}
                onReject={handleOpenRejectModal}
                onApprove={handleApproveStep}
              />
            )}
            {business.step2Status === MANUAL_KYC_STEP_STATUS.REJECTED && (
              <StepRejectedRemark
                stepLabel="Step 2"
                remark={getRejectRemark(business.step2Remarks, business.remarks)}
              />
            )}
          </Card>
        )}

        <Card
          size="small"
          title={
            <Space>
              <Text strong style={{ fontSize: 16 }}>
                {bankStep.title}
              </Text>
              <StepStatusTag status={bankStep.status} />
            </Space>
          }
          style={{
            marginBottom: 20,
            borderColor: rejectedBorder(bankStep.status),
          }}
        >
          <Descriptions
            bordered
            size="small"
            column={3}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Account Number">
              {displayOrPlaceholder(bank.accNum || bank.accountNumber)}
            </Descriptions.Item>
            <Descriptions.Item label="IFSC Code">
              {displayOrPlaceholder(bank.Ifsc?.ifscCode || bank.ifscCode)}
            </Descriptions.Item>
            <Descriptions.Item label="Account Holder">
              {displayOrPlaceholder(
                bank.accHolderName || bank.accountHolderName,
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Bank Name / Branch">
              {formatBankNameBranch(bank)}
            </Descriptions.Item>
          </Descriptions>

          <Title level={5} style={{ fontSize: 14, marginBottom: 12 }}>
            Uploaded Documents
          </Title>
          <ManualKycDocCards
            docList={step3Docs}
            isEntityOrHufOrNbfc={isEntityOrHufOrNbfc}
            isHuf={isHuf}
          />

          {bankStep.status === MANUAL_KYC_STEP_STATUS.SUBMITTED && (
            <StepActionButtons
              step={bankStep.stepNum}
              onReject={handleOpenRejectModal}
              onApprove={handleApproveStep}
            />
          )}
          {bankStep.status === MANUAL_KYC_STEP_STATUS.REJECTED && (
            <StepRejectedRemark
              stepLabel={`Step ${bankStep.stepNum}`}
              remark={getRejectRemark(bankStep.remarks, business.remarks)}
            />
          )}
        </Card>
      </Spin>

      <Modal
        title={`Reject Step ${rejectModal.step} Verification`}
        open={rejectModal.open}
        onCancel={handleCloseRejectModal}
        footer={[
          <CancelButton key="cancel" onClick={handleCloseRejectModal}/>,
          <DangerButton
            key="submit"
            loading={submittingReject}
            onClick={handleConfirmReject}
          >
            {MANUAL_KYC_MSG.CONFIRM_REJECTION}
          </DangerButton>,
        ]}
      >
        <p style={{ marginBottom: 12 }}>
          Please specify the reason for rejecting Step {rejectModal.step}:
        </p>
        <Input.TextArea
          rows={4}
          placeholder="Enter reason for rejection (required)..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default ManualKycReviewScreen;
